import {observable, action, makeObservable} from "mobx";
import {ServiceBase} from "../ServiceBase";
import {IServiceFactoryExtended} from '../service-factory-extended.interface';
import { DomEventSubscription } from '../../../utils/event-listener-registration';
import {IOnboardingStep, IOnboardingTourService} from "./onboarding-tour.service.interface";
import {OnboardingWelcomeStep} from "./steps/welcome.step";
import {OnboardingMainMenuStep} from "./steps/main-menu.step";
import {OnboardingUserMenuStep} from "./steps/user-menu.step";
import {FeatureId} from "../features-toggle-management/FeatureId";

export class OnboardingTourService extends ServiceBase implements IOnboardingTourService {
    private _onScrollSubscription: DomEventSubscription | null = null;

    readonly steps: IOnboardingStep[] = [];
    @observable.ref
    isOpened = false;
    @observable.ref
    stepIndex = 0;
    @observable.ref
    isThereRunningApps = true;

    private get isEnabledAccordingToRegistrationTime() {
        if(this.services.configuration.data.onboardingTour?.enabledAccordingToRegistrationTime) {
            return this.services.user.daysSinceRegistration < 30;
        }
        return true;
    }


    private get enabled(): boolean {
        return this.services.abTestingFeatureDataProvider.onboardingTour.isEnabled
                && this.isEnabledAccordingToRegistrationTime
                && this.steps.length > 0;
    }




    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        try {
            let configSteps = this.services.configuration.data.onboardingTour?.stepIds || [];
            this.steps = configSteps.map((configStepKey) => this.getStepInstance(configStepKey));
        } catch (e) {
            this.services.logger.error('Failed to configure onboarding tour steps', e);
        }

        this.services.appLauncher.then(appLauncher => {
            this.reaction(() => appLauncher.isThereRunningApps, (isThereRunningApps) => {
                this.isThereRunningApps = isThereRunningApps;
            }, {
                fireImmediately: true
            });
        })



        this.reaction(
            () => this.services.user.isAuthorized,
            (authorized) => this._onOauthChange(authorized),
            {
                fireImmediately: true
            }
        );

    }
    private _onOauthChange = async (authorized: boolean) => {
        this._removeScrollSubscription();

        if(!authorized || !this.enabled) {
            return;
        }

        const shouldShowFeature = await this.services.featuresToggleManagement.shouldShowFeature(FeatureId.OnboardingTour);
        if (shouldShowFeature) {
            // set a scroll event listener because the onboarding should be shown as soon as the user start's scrolling
            this._onScrollSubscription = this.services.window.subscribeTo.scroll(this._onWindowScroll);
        }
    };

    private _removeScrollSubscription = () => {
        // remove scroll event listener
        if (this._onScrollSubscription) {
            this._onScrollSubscription.dispose();
            this._onScrollSubscription = null;
        }
    };
    private _onWindowScroll = () => {
        // Check if this.services.routing.currentRoute?.data?.arenaType because we show onboarding only for home page and arenas
        if (this.services.routing.currentRoute?.data?.arenaType && this.services.window.scrollY > 200) {
            this.open();
        }
    };

    @action
    open() {
        if (!this.enabled) {
            return;
        }
        // Set that onboarding was shown
        this.services.featuresToggleManagement.updateFeatureCounter(FeatureId.OnboardingTour);

        // Remove scroll subscription
        this._removeScrollSubscription();
        // reset step index is for testing purposes
        this.stepIndex = 0;
        this.isOpened = true;
    }
    @action
    close() {
        this.isOpened = false;
    }

    @action
    nextStep() {
        this.stepIndex = Math.min(this.stepIndex + 1, this.steps.length - 1);
        return this.stepIndex;
    }

    private getStepInstance(configStepKey: string) {
        switch (configStepKey) {
            case "welcomeStep":
                return new OnboardingWelcomeStep();
            case "mainMenuStep":
                return new OnboardingMainMenuStep(this.services);
            case "userMenuStep":
                return new OnboardingUserMenuStep(this.services);
            default:
                throw new Error(`No step for this key ${configStepKey}`);
        }
    }

    // This code is very similar to the display getter from on-boarding-tooltip.model.ts difference is that one counts all overlay consumers except himself.
    // That's why this display will return true after on-boarding-tooltip.model.ts.
    // But because those two cases are particular and specific I choose not to extract this method in a more generic one.
    get allClearToDisplayOnboarding(): boolean {
        const orbitOverlayConsumersCount = this.services.overlay.countCurrentConsumers(consumer => {
            return consumer.needsOverlay;
        });
        const areRunningApps = this.isThereRunningApps;
        const hasDialogs = this.services.dialog.countDialogs({includeHidden: true}) > 0;

        //The reason we put the values in those variables is to make sure that this display getter depends on all these observables.
        return !(areRunningApps
            || hasDialogs
            || orbitOverlayConsumersCount > 0);
    }
}
